(function () {
  /**
   * DSC MVP - Gallery Sync (No-scroll, Spotlight v15.4.1)
   *
   * Goal:
   * - On variant change (input[name=id] change), activate the matching featured media
   * - DO NOT call media-gallery.setActiveMedia (because it may window.scrollTo)
   * - Instead, replicate only the SAFE parts:
   *   - toggle is-active on slides
   *   - horizontal scroll to slide
   *   - update thumbnail aria-current + horizontal thumb scroll
   *   - load deferred media (video/model) like Shopify does
   */

  const qs = (el, s) => (el ? el.querySelector(s) : null);

  const safeJson = (el, fb) => {
    try {
      return el ? JSON.parse(el.textContent || "") : fb;
    } catch {
      return fb;
    }
  };

  // ---- Build VariantId -> FeaturedMediaId map from your MVP variants JSON ----
  let variantToMediaMap = null;

  function getVariantToMediaMap() {
    if (variantToMediaMap) return variantToMediaMap;

    const root = document.querySelector(".dsc-mvp");
    if (!root) return null;

    const variants = safeJson(qs(root, ".dsc-mvp__variants"), []);
    if (!Array.isArray(variants) || !variants.length) return null;

    const map = new Map();
    for (const v of variants) {
      const vid = Number(v?.id);
      const mid = v?.featured_media?.id ? Number(v.featured_media.id) : null;
      if (vid && mid) map.set(vid, mid);
    }

    variantToMediaMap = map;
    return variantToMediaMap;
  }

  // ---- Helpers to find Spotlight section + media-gallery ----
  function getSectionId() {
    // Spotlight product section uses <product-info data-section="...">
    const pi = document.querySelector("product-info[data-section]");
    return pi?.getAttribute("data-section") || null;
  }

  function getMediaGallery() {
    return document.querySelector("media-gallery");
  }

  // ---- SAFE media activation (NO window scroll) ----
  function pauseAllMediaSafe() {
    try {
      if (typeof window.pauseAllMedia === "function") window.pauseAllMedia();
    } catch (_) {}
  }

  function loadDeferredMediaSafe(activeMediaEl) {
    try {
      const deferred = activeMediaEl?.querySelector?.(".deferred-media");
      if (deferred && typeof deferred.loadContent === "function") deferred.loadContent(false);
    } catch (_) {}
  }

  function setActiveThumbnailNoScroll(thumbnailsEl, targetKey) {
    if (!thumbnailsEl) return;

    const target = thumbnailsEl.querySelector(`[data-target="${targetKey}"]`);
    if (!target) return;

    // Remove aria-current
    thumbnailsEl.querySelectorAll("button").forEach((b) => b.removeAttribute("aria-current"));

    const btn = target.querySelector("button");
    if (btn) btn.setAttribute("aria-current", "true");

    // Horizontal thumb rail scroll if available (no vertical scroll)
    try {
      // Shopify slider-component exposes:
      // - thumbnailsEl.isSlideVisible(el, gap)
      // - thumbnailsEl.slider.scrollTo({ left })
      if (typeof thumbnailsEl.isSlideVisible === "function") {
        if (thumbnailsEl.isSlideVisible(target, 10)) return;
      }
      if (thumbnailsEl.slider && typeof thumbnailsEl.slider.scrollTo === "function") {
        thumbnailsEl.slider.scrollTo({ left: target.offsetLeft });
      }
    } catch (_) {}
  }

  function announceLiveRegionSafe(liveRegionEl, activeMediaEl, position) {
    // Optional. Shopify uses this for accessibility announcements.
    // Keep it minimal and safe.
    try {
      if (!liveRegionEl || !activeMediaEl) return;
      const img = activeMediaEl.querySelector(".product__modal-opener--image img");
      if (!img) return;
      liveRegionEl.setAttribute("aria-hidden", "false");
      liveRegionEl.textContent = `Media ${position || ""}`.trim();
      setTimeout(() => liveRegionEl.setAttribute("aria-hidden", "true"), 800);
    } catch (_) {}
  }

  function activateMediaNoScroll(mediaKey) {
    const mg = getMediaGallery();
    if (!mg) return false;

    const viewer = mg.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return false;

    const thumbnails = mg.querySelector('[id^="GalleryThumbnails"]');
    const liveRegion = mg.querySelector('[id^="GalleryStatus"]');

    // Find target slide
    const activeMedia =
      viewer.querySelector(`[data-media-id="${mediaKey}"]`) || viewer.querySelector("[data-media-id]");

    if (!activeMedia) return false;

    // Toggle is-active class like Shopify
    viewer.querySelectorAll("[data-media-id]").forEach((el) => el.classList.remove("is-active"));
    activeMedia.classList.add("is-active");

    // Horizontal scroll to slide (viewer is a slider-container)
    // Important: DO NOT do any vertical scrolling.
    try {
      if (!window.matchMedia("(min-width: 750px)").matches || thumbnails) {
        activeMedia.parentElement && activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
      }
    } catch (_) {}

    // Load deferred media like Shopify does
    pauseAllMediaSafe();
    loadDeferredMediaSafe(activeMedia);

    // Update thumbnail states (aria-current) + horizontal thumb scroll
    if (thumbnails) {
      setActiveThumbnailNoScroll(thumbnails, mediaKey);

      // If thumbnail has mediaPosition we can announce
      const t = thumbnails.querySelector(`[data-target="${mediaKey}"]`);
      const pos = t?.dataset?.mediaPosition;
      announceLiveRegionSafe(liveRegion, activeMedia, pos);
    }

    return true;
  }

  // ---- Sync entrypoint: variantId -> featuredMediaId -> mediaKey ----
  function syncFromVariantId(variantId) {
    const map = getVariantToMediaMap();
    if (!map) return;

    const vid = Number(variantId);
    const mediaId = map.get(vid);
    if (!mediaId) return;

    const sectionId = getSectionId();
    if (!sectionId) return;

    const mediaKey = `${sectionId}-${mediaId}`;

    // Retry a few frames in case gallery is still rendering
    let tries = 0;
    const maxTries = 12;

    const tick = () => {
      tries += 1;
      const ok = activateMediaNoScroll(mediaKey);
      if (ok || tries >= maxTries) return;
      requestAnimationFrame(tick);
    };

    tick();
  }

  // ---- Listen: main theme variant input[name=id] changes ----
  document.addEventListener(
    "change",
    (e) => {
      const inp = e.target;
      if (!inp || inp.name !== "id") return;

      // Only react inside product-info (Spotlight)
      const inProductInfo = inp.closest && inp.closest("product-info");
      if (!inProductInfo) return;

      syncFromVariantId(inp.value);
    },
    true
  );

  // Also support theme events (optional)
  document.addEventListener("variant:change", (e) => {
    const id = e?.detail?.variant?.id;
    if (id) syncFromVariantId(id);
  });

  // Initial sync
  document.addEventListener("DOMContentLoaded", () => {
    const pi = document.querySelector("product-info");
    const idInput = pi ? pi.querySelector('input[name="id"]') : document.querySelector('input[name="id"]');
    if (idInput?.value) syncFromVariantId(idInput.value);
  });
})();
